import { RefreshTokenExpiredError, refreshTokenWithDeviceInfo } from './utils/refreshTokenUtils';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
// In-memory storage for temporary OIDC flow data
let oidcFlowData = {};
// Flag to prevent duplicate callback processing
let callbackProcessed = false;
// Helper functions for in-memory storage
const setOidcFlowData = (key, value) => {
    oidcFlowData[key] = value;
};
const getOidcFlowData = (key) => {
    return oidcFlowData[key];
};
const clearOidcFlowData = () => {
    oidcFlowData = {};
};
const OidcContext = createContext(undefined);
export function useOidc() {
    const context = useContext(OidcContext);
    if (context === undefined) {
        throw new Error('useOidc must be used within an OidcProvider');
    }
    return context;
}
export function OidcProvider({ children, platformAdapter }) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [oidcSettings, setOidcSettings] = useState(null);
    const [isTokenMonitoring, setIsTokenMonitoring] = useState(false);
    const [currentPlatformAdapter, setCurrentPlatformAdapter] = useState(null);
    // Cancellation handler ref (e.g., to re-open modals)
    const cancelHandlerRef = useRef(null);
    // Use ref to access current oidcSettings in callback handlers
    const oidcSettingsRef = useRef(null);
    // Token monitoring refs
    const tokenMonitoringIntervalRef = useRef(null);
    const getTokenInfoRef = useRef(null);
    // Keep ref in sync with state
    useEffect(() => {
        oidcSettingsRef.current = oidcSettings;
    }, [oidcSettings]);
    // Cleanup token monitoring on unmount
    useEffect(() => {
        return () => {
            if (tokenMonitoringIntervalRef.current) {
                clearInterval(tokenMonitoringIntervalRef.current);
            }
        };
    }, []);
    // Refresh token function
    const refreshTokenMethod = useCallback(async (refreshToken) => {
        const currentSettings = oidcSettingsRef.current;
        if (!currentSettings) {
            throw new Error('OIDC service not initialized');
        }
        // Check if required device parameters are configured
        if (!currentSettings.deviceId ||
            !currentSettings.deviceName ||
            !currentSettings.deviceType ||
            !currentSettings.deviceVersion) {
            throw new Error('Device parameters not configured. Please configure deviceId, deviceName, deviceType, and deviceVersion in OIDC config.');
        }
        const tokenEndpoint = `${currentSettings.authority}/connect/token`;
        const deviceInfo = {
            deviceId: currentSettings.deviceId,
            deviceName: currentSettings.deviceName,
            deviceType: currentSettings.deviceType,
            deviceVersion: currentSettings.deviceVersion
        };
        const result = await refreshTokenWithDeviceInfo({
            refreshToken,
            clientId: currentSettings.clientId,
            clientSecret: currentSettings.clientSecret || '',
            tokenEndpoint,
            deviceInfo
        });
        return result;
    }, []);
    // Token monitoring functions
    const startTokenMonitoring = useCallback((getTokenInfo) => {
        const currentSettings = oidcSettingsRef.current;
        if (!(currentSettings === null || currentSettings === void 0 ? void 0 : currentSettings.autoRenew)) {
            return;
        }
        getTokenInfoRef.current = getTokenInfo;
        setIsTokenMonitoring(true);
        const checkInterval = currentSettings.checkInterval || 60000; // Default 1 minute
        const renewBeforeExpiry = currentSettings.renewBeforeExpiry || 300; // Default 5 minutes
        tokenMonitoringIntervalRef.current = window.setInterval(() => {
            var _a;
            const tokenInfo = (_a = getTokenInfoRef.current) === null || _a === void 0 ? void 0 : _a.call(getTokenInfoRef);
            if (!tokenInfo) {
                return;
            }
            const now = Math.floor(Date.now() / 1000);
            const timeUntilExpiry = tokenInfo.expiresAt - now;
            if (timeUntilExpiry <= renewBeforeExpiry && tokenInfo.refreshToken) {
                refreshTokenMethod(tokenInfo.refreshToken)
                    .then((newTokenInfo) => {
                    // Token renewed successfully
                })
                    .catch((error) => {
                    console.error('❌ Token renewal failed:', error);
                    if (error instanceof RefreshTokenExpiredError) {
                        stopTokenMonitoring();
                    }
                });
            }
        }, checkInterval);
    }, [refreshTokenMethod]);
    const stopTokenMonitoring = useCallback(() => {
        if (tokenMonitoringIntervalRef.current) {
            clearInterval(tokenMonitoringIntervalRef.current);
            tokenMonitoringIntervalRef.current = null;
        }
        setIsTokenMonitoring(false);
        getTokenInfoRef.current = null;
    }, []);
    // Initialize OIDC service
    const initialize = useCallback(async (config, adapter) => {
        setOidcSettings(config);
        setCurrentPlatformAdapter(adapter || null);
        setIsInitialized(true);
        // Set up callback handler if platform adapter is provided
        // Only set up callback handler if not already set to avoid replacing during auth flow
        if (adapter && (!isLoading || Object.keys(oidcFlowData).length === 0)) {
            adapter.handleAuthCallback((data) => {
                var _a;
                if (callbackProcessed) {
                    return;
                }
                callbackProcessed = true;
                // Clear the timeout
                const timeoutId = getOidcFlowData('timeoutId');
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                if (data.error) {
                    console.error('🔍 OIDC callback error:', data.error);
                    // Handle user cancellation specifically
                    if (data.error === 'Authentication cancelled by user') {
                        setIsLoading(false);
                        clearOidcFlowData();
                        // Invoke optional cancellation handler
                        try {
                            (_a = cancelHandlerRef.current) === null || _a === void 0 ? void 0 : _a.call(cancelHandlerRef);
                        }
                        catch (_b) { }
                        return;
                    }
                    setError(new Error(`Authentication failed: ${data.error}`));
                    setIsLoading(false);
                    clearOidcFlowData();
                    return;
                }
                if (!data.code || !data.state) {
                    console.error('🔍 OIDC callback missing code or state');
                    setError(new Error('Invalid authentication response'));
                    setIsLoading(false);
                    clearOidcFlowData();
                    return;
                }
                // Verify state
                const expectedState = getOidcFlowData('state');
                console.log('🔍 OIDC callback: Current flow data before validation:', {
                    allFlowData: oidcFlowData,
                    hasState: 'state' in oidcFlowData,
                    stateValue: oidcFlowData.state
                });
                console.log('🔍 OIDC state validation:', {
                    received: data.state,
                    expected: expectedState,
                    match: data.state === expectedState
                });
                if (data.state !== expectedState) {
                    console.error('🔍 OIDC state mismatch - Expected:', expectedState, 'Received:', data.state);
                    setError(new Error('Authentication state mismatch'));
                    setIsLoading(false);
                    clearOidcFlowData();
                    return;
                }
                // Exchange code for tokens
                exchangeCodeForTokens(data.code)
                    .then((tokenResponse) => {
                    setIsLoading(false);
                    // Call the success callback BEFORE clearing flow data
                    const successCallback = getOidcFlowData('successCallback');
                    if (successCallback) {
                        successCallback(tokenResponse);
                    }
                    // Clear flow data after calling the callback
                    clearOidcFlowData();
                })
                    .catch((error) => {
                    console.error('❌ OIDC token exchange failed:', error);
                    setError(error);
                    setIsLoading(false);
                    clearOidcFlowData();
                });
            });
        }
    }, []);
    // Handle user cancellation
    const handleUserCancellation = useCallback(() => {
        // Clear the timeout if it exists
        const timeoutId = getOidcFlowData('timeoutId');
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        setIsLoading(false);
        clearOidcFlowData();
    }, []);
    // Sign in function
    const signIn = useCallback(async (loginHint, onSuccess, prompt) => {
        if (!currentPlatformAdapter) {
            throw new Error('OIDC platform adapter not provided');
        }
        const currentSettings = oidcSettingsRef.current;
        if (!currentSettings) {
            throw new Error('OIDC service not initialized');
        }
        try {
            setIsLoading(true);
            setError(null);
            clearOidcFlowData();
            // Reset callback processed flag
            callbackProcessed = false;
            // Generate PKCE parameters
            const generateCodeVerifier = () => {
                const array = new Uint8Array(32);
                crypto.getRandomValues(array);
                return btoa(String.fromCharCode(...array))
                    .replace(/\+/g, '-')
                    .replace(/\//g, '_')
                    .replace(/=/g, '');
            };
            const generateCodeChallenge = (verifier) => {
                const encoder = new TextEncoder();
                const data = encoder.encode(verifier);
                return crypto.subtle
                    .digest({ name: 'SHA-256' }, data)
                    .then((hash) => {
                    return btoa(String.fromCharCode(...new Uint8Array(hash)))
                        .replace(/\+/g, '-')
                        .replace(/\//g, '_')
                        .replace(/=/g, '');
                });
            };
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = await generateCodeChallenge(codeVerifier);
            const state = Math.random().toString(36).substring(2);
            // Store PKCE parameters in memory
            setOidcFlowData('codeVerifier', codeVerifier);
            setOidcFlowData('state', state);
            if (onSuccess) {
                setOidcFlowData('successCallback', onSuccess);
            }
            // Build authorization URL
            const authUrl = new URL(`${currentSettings.authority}/connect/authorize`);
            authUrl.searchParams.set('response_type', 'code');
            authUrl.searchParams.set('client_id', currentSettings.clientId);
            authUrl.searchParams.set('redirect_uri', currentSettings.redirectUri);
            authUrl.searchParams.set('scope', currentSettings.scope);
            authUrl.searchParams.set('state', state);
            authUrl.searchParams.set('code_challenge', codeChallenge);
            authUrl.searchParams.set('code_challenge_method', 'S256');
            // Force login prompt to prevent automatic login with existing session
            const promptValue = String(prompt || currentSettings.prompt || 'login');
            authUrl.searchParams.set('prompt', promptValue);
            // Add device parameters
            if (currentSettings.deviceId) {
                authUrl.searchParams.set('deviceid', currentSettings.deviceId);
            }
            if (currentSettings.deviceName) {
                authUrl.searchParams.set('devicename', currentSettings.deviceName);
            }
            if (currentSettings.deviceType) {
                authUrl.searchParams.set('devicetype', currentSettings.deviceType);
            }
            if (currentSettings.deviceVersion) {
                authUrl.searchParams.set('deviceversion', currentSettings.deviceVersion);
            }
            // Add login hint if provided
            if (loginHint) {
                authUrl.searchParams.set('login_hint', loginHint);
            }
            // Open popup using platform adapter
            await currentPlatformAdapter.openAuthPopup(authUrl.toString());
            // Set a timeout to handle cases where the popup doesn't respond
            const timeout = setTimeout(() => {
                setIsLoading(false);
                setError(new Error('Authentication timeout - please try again'));
                clearOidcFlowData();
            }, 120000); // 2 minutes timeout
            // Store the timeout ID in memory
            setOidcFlowData('timeoutId', timeout);
        }
        catch (err) {
            setError(err instanceof Error ? err : new Error('Sign in failed'));
            setIsLoading(false);
            clearOidcFlowData();
        }
    }, [currentPlatformAdapter]);
    // Exchange authorization code for tokens
    const exchangeCodeForTokens = useCallback(async (code) => {
        const currentSettings = oidcSettingsRef.current;
        if (!currentSettings) {
            throw new Error('OIDC service not initialized');
        }
        const tokenEndpoint = `${currentSettings.authority}/connect/token`;
        // Get the stored code verifier from memory
        const codeVerifier = getOidcFlowData('codeVerifier');
        const body = new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: currentSettings.clientId,
            client_secret: currentSettings.clientSecret || '',
            code: code,
            redirect_uri: currentSettings.redirectUri,
            code_verifier: codeVerifier || ''
        });
        // Add device parameters to token request
        if (currentSettings.deviceId) {
            body.append('deviceid', currentSettings.deviceId);
        }
        if (currentSettings.deviceName) {
            body.append('devicename', currentSettings.deviceName);
        }
        if (currentSettings.deviceType) {
            body.append('devicetype', currentSettings.deviceType);
        }
        if (currentSettings.deviceVersion) {
            body.append('deviceversion', currentSettings.deviceVersion);
        }
        const response = await fetch(tokenEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: body.toString()
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error('Token exchange error response:', errorText);
            throw new Error(`Token exchange failed: ${response.statusText} - ${errorText}`);
        }
        const responseData = await response.json();
        return responseData;
    }, []);
    const clearError = useCallback(() => {
        setError(null);
    }, []);
    const getUserInfo = useCallback(async (accessToken) => {
        const currentSettings = oidcSettingsRef.current;
        if (!currentSettings) {
            throw new Error('OIDC service not initialized');
        }
        const userInfoEndpoint = `${currentSettings.authority}/connect/userinfo`;
        try {
            const response = await fetch(userInfoEndpoint, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error('UserInfo error response:', errorText);
                throw new Error(`UserInfo request failed: ${response.statusText} - ${errorText}`);
            }
            const userInfo = await response.json();
            return userInfo;
        }
        catch (error) {
            console.error('Failed to fetch user info:', error);
            throw error;
        }
    }, []);
    const onCancel = useCallback((handler) => {
        cancelHandlerRef.current = handler || null;
    }, []);
    const value = useMemo(() => ({
        isLoading,
        error,
        isInitialized,
        signIn,
        initialize,
        clearError,
        handleUserCancellation,
        refreshToken: refreshTokenMethod,
        getUserInfo,
        startTokenMonitoring,
        stopTokenMonitoring,
        isTokenMonitoring,
        onCancel
    }), [
        isLoading,
        error,
        isInitialized,
        signIn,
        initialize,
        clearError,
        handleUserCancellation,
        refreshTokenMethod,
        getUserInfo,
        startTokenMonitoring,
        stopTokenMonitoring,
        isTokenMonitoring,
        onCancel
    ]);
    return React.createElement(OidcContext.Provider, { value: value }, children);
}
